import React, { useEffect, useRef, useState } from 'react';
import { DeploymentConfig } from '../Model/Deployment';
import { userDashboard } from '../Service/UserService';
import axiosInstance from '../Core/RestHelper';

interface Props {
  config: DeploymentConfig;
  updateConfig: (updates: Partial<DeploymentConfig>) => void;
  onBack: () => void;
  onNext: () => void;
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface AwsAccountResult {
  role_arn: string;
  region: string;
  account_id: string;
  success: boolean;
  message: string;
  missing_permissions: string[];
  remarks: string[];
}

interface AzureAccountResult {
  tenant_id: string;
  subscription_id: string;
  region: string;
  success: boolean;
  message: string;
  granted_roles: string[];
  missing_permissions: string[];
  remarks: string[];
}

interface CloudAccount {
  key: string;
  cloud_type: 'aws' | 'azure';
  label: string;
  sub_label: string;
  region: string;
  success: boolean;
  cloud_ref_id: string;
}

// ── Toggle ────────────────────────────────────────────────────────────────────

const Toggle: React.FC<{ on: boolean; onClick: () => void; disabled?: boolean }> = ({ on, onClick, disabled }) => (
  <div
    onClick={disabled ? undefined : onClick}
    style={{
      width: '38px', height: '21px', borderRadius: '11px',
      background: on && !disabled ? '#6366f1' : '#1e1e27',
      border: `1px solid ${on && !disabled ? '#6366f1' : '#32323d'}`,
      position: 'relative' as const, cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'background .2s, border-color .2s', flexShrink: 0,
      opacity: disabled ? 0.4 : 1,
    }}
  >
    <div style={{
      position: 'absolute' as const, width: '15px', height: '15px',
      borderRadius: '50%', background: '#fff', top: '2px',
      left: on && !disabled ? '19px' : '2px', transition: 'left .2s',
    }} />
  </div>
);

// ── Cloud Account Dropdown ────────────────────────────────────────────────────

const CloudAccountDropdown: React.FC<{
  accounts: CloudAccount[];
  selectedKey: string | undefined;
  onSelect: (account: CloudAccount) => void;
}> = ({ accounts, selectedKey, onSelect }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const selected = accounts.find(a => a.cloud_ref_id === selectedKey && a.success);

  const iconMap  = { aws: '🟠', azure: '🔷' };
  const colorMap = { aws: '#ff9900', azure: '#0078d4' };
  const bgMap    = { aws: 'rgba(255,153,0,.12)', azure: 'rgba(0,120,212,.12)' };

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const connectedAccounts = accounts.filter(a => a.success);
  const failedAccounts    = accounts.filter(a => !a.success);

  return (
    <div ref={ref} style={{ position: 'relative' as const }}>

      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: '10px',
          padding: '11px 14px', borderRadius: '8px', cursor: 'pointer',
          background: '#17171e',
          border: `1px solid ${open ? '#6366f1' : selected ? '#6366f1' : '#32323d'}`,
          outline: 'none', textAlign: 'left' as const, transition: 'border-color .15s',
        }}
      >
        {selected ? (
          <>
            <div style={{ width: '28px', height: '28px', borderRadius: '6px', background: bgMap[selected.cloud_type], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', flexShrink: 0 }}>
              {iconMap[selected.cloud_type]}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#eeeef5', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>
                {selected.label}
              </div>
              <div style={{ fontSize: '10.5px', color: '#52525f', fontFamily: "'IBM Plex Mono',monospace", marginTop: '1px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', fontSize: '10px', fontWeight: 700, padding: '1px 5px', borderRadius: '3px', background: bgMap[selected.cloud_type], color: colorMap[selected.cloud_type] }}>
                  {selected.cloud_type.toUpperCase()}
                </span>
                <span>{selected.region}</span>
              </div>
            </div>
          </>
        ) : (
          <span style={{ fontSize: '13px', color: '#52525f', flex: 1 }}>Select a cloud account…</span>
        )}

        {/* Chevron */}
        <svg
          width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#52525f" strokeWidth="1.5"
          style={{ flexShrink: 0, transition: 'transform .2s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
        >
          <path d="M3 5l4 4 4-4"/>
        </svg>
      </button>

      {/* Dropdown panel */}
      {open && (
        <div style={{
          position: 'absolute' as const, top: 'calc(100% + 5px)', left: 0, right: 0, zIndex: 200,
          background: '#17171e', border: '1px solid #32323d', borderRadius: '8px',
          boxShadow: '0 8px 24px rgba(0,0,0,.5)', overflow: 'hidden',
        }}>
          {connectedAccounts.length === 0 && (
            <div style={{ padding: '14px 16px', fontSize: '12.5px', color: '#52525f', textAlign: 'center' as const }}>
              No connected accounts available
            </div>
          )}

          {connectedAccounts.map(acc => (
            <div
              key={acc.key}
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect(acc);
                setOpen(false);
              }}
              style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                padding: '10px 14px', cursor: 'pointer',
                background: selectedKey === acc.cloud_ref_id ? 'rgba(99,102,241,.12)' : 'transparent',
                borderBottom: '1px solid #26262f',
                transition: 'background .1s',
              }}
              onMouseEnter={e => { if (selectedKey !== acc.cloud_ref_id) (e.currentTarget as HTMLElement).style.background = '#1e1e27'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = selectedKey === acc.cloud_ref_id ? 'rgba(99,102,241,.12)' : 'transparent'; }}
            >
              <div style={{ width: '28px', height: '28px', borderRadius: '6px', background: bgMap[acc.cloud_type], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', flexShrink: 0 }}>
                {iconMap[acc.cloud_type]}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: '#eeeef5', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>
                  {acc.label}
                </div>
                <div style={{ fontSize: '10.5px', color: '#52525f', fontFamily: "'IBM Plex Mono',monospace", marginTop: '1px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', fontSize: '10px', fontWeight: 700, padding: '1px 5px', borderRadius: '3px', background: bgMap[acc.cloud_type], color: colorMap[acc.cloud_type] }}>
                    {acc.cloud_type.toUpperCase()}
                  </span>
                  <span>{acc.sub_label}</span>
                  <span style={{ color: '#32323d' }}>·</span>
                  <span>{acc.region}</span>
                </div>
              </div>
              {selectedKey === acc.cloud_ref_id && (
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="#6366f1" strokeWidth="2" style={{ flexShrink: 0 }}>
                  <path d="M2 6.5l4 4 5-5"/>
                </svg>
              )}
            </div>
          ))}

          {/* Failed accounts — shown greyed out, not selectable */}
          {failedAccounts.map(acc => (
            <div
              key={acc.key}
              style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                padding: '10px 14px', opacity: 0.4, cursor: 'not-allowed',
                borderBottom: '1px solid #26262f',
              }}
            >
              <div style={{ width: '28px', height: '28px', borderRadius: '6px', background: bgMap[acc.cloud_type], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', flexShrink: 0 }}>
                {iconMap[acc.cloud_type]}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: '#eeeef5', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>
                  {acc.label}
                </div>
                <div style={{ fontSize: '10.5px', color: '#52525f', marginTop: '1px' }}>{acc.region}</div>
              </div>
              <span style={{ fontSize: '10px', fontWeight: 600, color: '#f43f5e', background: 'rgba(244,63,94,.1)', padding: '2px 6px', borderRadius: '4px', flexShrink: 0 }}>
                Failed
              </span>
            </div>
          ))}

          {/* Footer link */}
          <div style={{ padding: '8px 14px', borderTop: failedAccounts.length > 0 || connectedAccounts.length > 0 ? '1px solid #26262f' : 'none' }}>
            <a
              href="/cloud"
              style={{ fontSize: '12px', color: '#818cf8', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}
            >
              <svg width="11" height="11" viewBox="0 0 11 11" fill="currentColor"><path d="M5.5 1v9M1 5.5h9"/></svg>
              Manage cloud accounts
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Deployment options ────────────────────────────────────────────────────────

const deploymentOptions = [
  { type: 'EC2' as const, label: 'EC2', sub: 'Available', disabled: false },
  { type: 'ECS' as const, label: 'ECS', sub: 'Coming Soon', disabled: true },
  { type: 'EKS' as const, label: 'EKS', sub: 'Coming Soon', disabled: true },
];

// ── StepTwo ───────────────────────────────────────────────────────────────────

const StepTwo: React.FC<Props> = ({ config, updateConfig, onBack, onNext }) => {
  const [isMonitoringEnabled, setIsMonitoringEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [cloudAccounts, setCloudAccounts] = useState<CloudAccount[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);

  // ── Fetch plan perks ────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const res = await userDashboard();
        if (res.status === 200) setIsMonitoringEnabled(res.data?.plan_details?.perks?.monitor_dashboard !== null);
      } catch {
        setIsMonitoringEnabled(false);
      } finally {
        setLoading(false);
      }
    };
    fetchUserData();
  }, []);

  // ── Fetch cloud accounts ────────────────────────────────────────────────────
  useEffect(() => {
    const fetchAccounts = async () => {
      setLoadingAccounts(true);
      const accounts: CloudAccount[] = [];

      try {
        const awsRes = await axiosInstance.post('/aws/verify');
        const awsData: { accounts: AwsAccountResult[] } = awsRes.data;
        awsData.accounts.forEach(acc => {
          accounts.push({
            key: acc.role_arn,
            cloud_type: 'aws',
            label: `AWS — ${acc.account_id}`,
            sub_label: acc.account_id,
            region: acc.region,
            success: acc.success,
            cloud_ref_id: acc.role_arn,
          });
        });
      } catch { /* no AWS accounts */ }

      try {
        const azureRes = await axiosInstance.post('/azure/verify');
        const azureData: { accounts: AzureAccountResult[] } = azureRes.data;
        azureData.accounts.forEach(acc => {
          accounts.push({
            key: acc.subscription_id,
            cloud_type: 'azure',
            label: `Azure — ${acc.subscription_id}`,
            sub_label: acc.subscription_id,
            region: acc.region,
            success: acc.success,
            cloud_ref_id: acc.subscription_id,
          });
        });
      } catch { /* no Azure accounts */ }

      setCloudAccounts(accounts);
      setLoadingAccounts(false);
    };

    fetchAccounts();
  }, []);

  const handleAccountSelect = (account: CloudAccount) => {
    updateConfig({
      selected_cloud: account.cloud_type.toUpperCase(),
      cloud_ref_id: account.cloud_ref_id,
    });
  };

  const hasConnectedAccounts = cloudAccounts.some(a => a.success);

  // User must explicitly pick from dropdown — no auto-select
  const accountSelected = !!config.cloud_ref_id && !!config.selected_cloud;
  const canProceed = accountSelected && !!config.service_selected;

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', gap: '12px' }}>
        <div style={{ width: '20px', height: '20px', borderRadius: '50%', border: '2px solid #26262f', borderTopColor: '#6366f1', animation: 'spin .8s linear infinite' }} />
        <span style={{ fontSize: '13px', color: '#52525f' }}>Loading plan details…</span>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
      <div>
        <div style={{ fontFamily: "'Syne',sans-serif", fontSize: '15px', fontWeight: 700, color: '#eeeef5', marginBottom: '3px' }}>Configure Deployment</div>
        <div style={{ fontSize: '12px', color: '#52525f' }}>Select your cloud account, compute service, and deployment options</div>
      </div>

      {/* ── Cloud Account Selector ── */}
      <div>
        <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '.08em', color: '#52525f', marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Target Cloud Account <span style={{ color: '#f43f5e' }}>*</span></span>
          {!loadingAccounts && (
            <a href="/cloud" style={{ fontSize: '11px', color: '#818cf8', textDecoration: 'none', fontWeight: 500, textTransform: 'none' as const }}>
              + Manage Accounts →
            </a>
          )}
        </div>

        {loadingAccounts ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '14px', background: '#17171e', border: '1px solid #26262f', borderRadius: '8px' }}>
            <div style={{ width: '16px', height: '16px', borderRadius: '50%', border: '2px solid #26262f', borderTopColor: '#6366f1', animation: 'spin .8s linear infinite', flexShrink: 0 }} />
            <span style={{ fontSize: '12.5px', color: '#52525f' }}>Loading cloud accounts…</span>
          </div>
        ) : cloudAccounts.length === 0 ? (
          <div style={{ padding: '24px 20px', background: '#17171e', border: '1px dashed #32323d', borderRadius: '8px', textAlign: 'center' as const }}>
            <div style={{ fontSize: '24px', marginBottom: '8px' }}>☁️</div>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#eeeef5', marginBottom: '4px' }}>No cloud accounts connected</div>
            <div style={{ fontSize: '12px', color: '#52525f', marginBottom: '14px' }}>
              You must connect an AWS or Azure account before deploying.
            </div>
            <a href="/cloud" style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '7px 14px', borderRadius: '6px', fontSize: '12.5px', fontWeight: 500, background: '#6366f1', color: '#fff', textDecoration: 'none' }}>
              Connect Cloud Account →
            </a>
          </div>
        ) : !hasConnectedAccounts ? (
          <div style={{ padding: '24px 20px', background: '#17171e', border: '1px solid rgba(244,63,94,.2)', borderRadius: '8px', textAlign: 'center' as const }}>
            <div style={{ fontSize: '24px', marginBottom: '8px' }}>⚠️</div>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#f43f5e', marginBottom: '4px' }}>All accounts failed verification</div>
            <div style={{ fontSize: '12px', color: '#52525f', marginBottom: '14px' }}>
              Fix the issues on your cloud accounts before deploying.
            </div>
            <a href="/cloud" style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '7px 14px', borderRadius: '6px', fontSize: '12.5px', fontWeight: 500, background: 'rgba(244,63,94,.1)', color: '#f43f5e', border: '1px solid rgba(244,63,94,.2)', textDecoration: 'none' }}>
              Fix Accounts →
            </a>
          </div>
        ) : (
          <CloudAccountDropdown
            accounts={cloudAccounts}
            selectedKey={config.cloud_ref_id}
            onSelect={handleAccountSelect}
          />
        )}
      </div>

      {/* ── Compute service ── */}
      <div>
        <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '.08em', color: '#52525f', marginBottom: '10px' }}>
          Compute Service <span style={{ color: '#f43f5e' }}>*</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '8px' }}>
          {deploymentOptions.map(opt => (
            <div
              key={opt.type}
              onClick={() => !opt.disabled && updateConfig({ service_selected: opt.type })}
              style={{
                border: `1px solid ${config.service_selected === opt.type && !opt.disabled ? '#6366f1' : '#26262f'}`,
                background: config.service_selected === opt.type && !opt.disabled ? 'rgba(99,102,241,.14)' : '#17171e',
                borderRadius: '6px', padding: '12px 14px',
                cursor: opt.disabled ? 'not-allowed' : 'pointer',
                transition: 'all .15s', opacity: opt.disabled ? 0.5 : 1,
                position: 'relative' as const,
              }}
            >
              <div style={{ fontSize: '13px', fontWeight: 600, color: config.service_selected === opt.type && !opt.disabled ? '#818cf8' : opt.disabled ? '#52525f' : '#eeeef5', marginBottom: '3px' }}>
                {opt.label}
              </div>
              <div style={{ fontSize: '11px', color: opt.disabled ? '#eab308' : '#22c55e' }}>{opt.sub}</div>
              {config.service_selected === opt.type && !opt.disabled && (
                <div style={{ position: 'absolute' as const, top: '8px', right: '8px' }}>
                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="#6366f1" strokeWidth="2"><path d="M2 6.5l4 4 5-5" /></svg>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ── Options toggles ── */}
      <div>
        <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '.08em', color: '#52525f', marginBottom: '10px' }}>
          Options
        </div>
        <div style={{ border: '1px solid #26262f', borderRadius: '6px', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderBottom: '1px solid #26262f' }}>
            <div>
              <div style={{ fontSize: '13px', fontWeight: 500, color: '#eeeef5' }}>Enable CI/CD</div>
              <div style={{ fontSize: '11.5px', color: '#52525f', marginTop: '2px' }}>Auto-deploy on every push to branch</div>
            </div>
            <Toggle on={!!config.ci_cd} onClick={() => updateConfig({ ci_cd: !config.ci_cd })} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px' }}>
            <div>
              <div style={{ fontSize: '13px', fontWeight: 500, color: isMonitoringEnabled ? '#eeeef5' : '#52525f' }}>Enable Monitoring</div>
              <div style={{ fontSize: '11.5px', color: '#52525f', marginTop: '2px' }}>Track performance, uptime & receive alerts</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {!isMonitoringEnabled && (
                <span style={{ fontSize: '10.5px', fontWeight: 600, padding: '2px 7px', borderRadius: '4px', background: 'linear-gradient(135deg,rgba(99,102,241,.08),rgba(168,85,247,.08))', border: '1px solid rgba(99,102,241,.2)', color: '#818cf8' }}>
                  Pro plan
                </span>
              )}
              <Toggle on={!!config.monitoring && isMonitoringEnabled} onClick={() => isMonitoringEnabled && updateConfig({ monitoring: !config.monitoring })} disabled={!isMonitoringEnabled} />
            </div>
          </div>
        </div>
      </div>

      {/* ── Blocking notice ── */}
      {!loadingAccounts && !hasConnectedAccounts && (
        <div style={{ padding: '10px 12px', background: 'rgba(244,63,94,.06)', border: '1px solid rgba(244,63,94,.2)', borderRadius: '6px', fontSize: '12px', color: '#f43f5e', display: 'flex', gap: '7px', alignItems: 'flex-start' }}>
          <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ flexShrink: 0, marginTop: '1px' }}>
            <circle cx="6.5" cy="6.5" r="5.5"/><path d="M6.5 3.5v3M6.5 9v.5"/>
          </svg>
          A connected cloud account is required to proceed. Please connect or fix an account on the{' '}
          <a href="/cloud" style={{ color: '#f43f5e', fontWeight: 600 }}>Cloud Accounts</a> page.
        </div>
      )}

      {/* Hint when account not yet selected */}
      {!loadingAccounts && hasConnectedAccounts && !accountSelected && (
        <div style={{ padding: '10px 12px', background: 'rgba(99,102,241,.06)', border: '1px solid rgba(99,102,241,.15)', borderRadius: '6px', fontSize: '12px', color: '#818cf8', display: 'flex', gap: '7px', alignItems: 'center' }}>
          <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ flexShrink: 0 }}>
            <circle cx="6.5" cy="6.5" r="5.5"/><path d="M6.5 3.5v3M6.5 9v.5"/>
          </svg>
          Please select a cloud account from the dropdown above to continue.
        </div>
      )}

      {/* ── Navigation ── */}
      <div style={{ paddingTop: '8px', borderTop: '1px solid #26262f', display: 'flex', justifyContent: 'space-between' }}>
        <button
          onClick={onBack}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '8px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: 500, cursor: 'pointer', background: '#17171e', border: '1px solid #32323d', color: '#eeeef5' }}
        >
          <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M10.5 6.5h-8M6 3.5L2.5 6.5 6 10"/></svg>
          Back
        </button>
        <button
          onClick={canProceed ? onNext : undefined}
          disabled={!canProceed}
          title={!accountSelected ? 'Select a cloud account' : !config.service_selected ? 'Select a compute service' : ''}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '5px',
            padding: '8px 20px', borderRadius: '6px', fontSize: '13px', fontWeight: 500,
            border: 'none', color: '#fff',
            background: !canProceed ? '#2a2a38' : '#6366f1',
            cursor: !canProceed ? 'not-allowed' : 'pointer',
            opacity: !canProceed ? 0.5 : 1,
            transition: 'background .15s, opacity .15s',
          }}
        >
          Continue to Review
          <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2.5 6.5h8M7 3l3.5 3.5L7 10"/></svg>
        </button>
      </div>

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

export default StepTwo;